import React, {useCallback, useState} from "react";
import {DocumentType, documentTypeToLabel} from "../../domain/verification/DocumentType";
import {RqInputDocument} from "../../api/dto/RqInputDocument";
import {InputComponent} from "../../components/common/input/InputComponent";
import {RsDocument, RsDocumentFieldDetails} from "../../api/dto/RsVerification";
import {ButtonComponent} from "../../components/common/button/ButtonComponent";
import ComboBoxComponent from "../../components/common/select/ComboBoxComponent";
import {InputDocument, InputDocumentAdditionalInformation} from "../../domain/document/InputDocument";
import {RqInputDocumentAdditionalInformation} from "../../api/dto/RqInputDocumentAdditionalInformation";

export const DocumentInformationViewModel = (onNextClick: () => void) => {

    const [documentNumber, setDocumentNumber] = useState("")
    const [documentType, setDocumentType] = useState<DocumentType>(DocumentType.OTHER)
    const [expirationDate, setExpirationDate] = useState("")
    const [version, setVersion] = useState("")
    const [fields, setFields] =
        useState<Map<string, RsDocumentFieldDetails>>()
    const [fieldsValue, setFieldsValue] = useState<Map<string, any>>(new Map())

    const [documentEditedInformation] =
        useState<RqInputDocument>(RqInputDocument.empty())

    const markAsEditedDocumentInfo = (field: string, func: (field: string, value: any, type: string) => void, value: string, type: string) => {
        if (field === "documentId") {
            setDocumentNumber(value)
            documentEditedInformation.documentNumber = value
        }
        if (fields) {
            const fieldsMap = new Map(Object.entries(fields))
            const fieldType = fieldsMap.get(field).type
            if (fieldType) {
                const existingField =
                    documentEditedInformation.additionalInformation.find(entry => entry.key === field)
                if (existingField) {
                    existingField.value = value
                } else {
                    const fieldInfo = new RqInputDocumentAdditionalInformation(field, fieldType, value)
                    documentEditedInformation.additionalInformation.push(fieldInfo)
                }
                func(field, value, type)
            }

        }

    }

    const upsertFieldValue = (field: string, value: any, type: string) => {
        const updatedFieldsValue: Map<string, any> = new Map(fieldsValue)
        updatedFieldsValue.set(field, value)
        setFieldsValue(updatedFieldsValue)
    }

    const buildFieldsDocumentInfoBlock = (show: boolean) => {
        return (
            <div className={`info-position header-back-container ${show ? '' : 'hide-important'}`}>
                <div className={`info-container ${show ? '' : 'hide-important'}`}>
                    <div className="info-container-title primary-color font-lg full-row">
                        Document information
                    </div>
                    <InputComponent
                        label="ID type"
                        disabled
                        value={documentTypeToLabel(documentType)}
                        onChange={(value) => {}}/>
                    {fields != null && fieldsValue != null && Object.entries(fields).map(([key, value]) => {
                        if (value.type !== 'combo') {
                            return (
                                <InputComponent
                                    label={value.title}
                                    type={value.type.toLowerCase()}
                                    value={fieldsValue.get(key)!!}
                                    digitsOnly={value.digitsOnly}
                                    maxLength={value.maxCharacters ? value.maxCharacters : undefined}
                                    onChange={(propValue) => markAsEditedDocumentInfo(key, upsertFieldValue, propValue, value.type)}/>
                            )
                        } else if (value.type === 'date') {
                            return <InputComponent
                                label={value.title}
                                type="date"
                                value={fieldsValue.get(key)!!}
                                digitsOnly={value.digitsOnly}
                                maxLength={value.maxCharacters ? value.maxCharacters : undefined}
                                onChange={(propValue) => markAsEditedDocumentInfo(key, upsertFieldValue, propValue, value.type)}/>
                        } else {
                            return (
                                <ComboBoxComponent
                                    options={value.values}
                                    id={key}
                                    name={key}
                                    label={value.title}
                                    onChange={(propValue) => markAsEditedDocumentInfo(key, upsertFieldValue, propValue, value.type)}
                                    placeholder={`Select ${value.title}`}
                                />
                            )
                        }
                    }

                    )}
                </div>
                <div className={`width-full info-step-button-container`}>
                    <ButtonComponent label="Next" onButtonClick={() => onNextClick()} className={`info-step-button`}
                                     disabled={!isEnabled()}/>
                </div>

            </div>
        )
    }

    const populateDocumentInformation = useCallback((document: RsDocument) => {
        setDocumentNumber(document.documentId)
        setDocumentType(document.type)
        setVersion(document.version)
        if (document.expirationDate) {
            setExpirationDate(new Date(document.expirationDate).toLocaleDateString("en-GB"))
        }
        setFields(document.fields)
        if (document.fields != null) {
            const fieldsValue = new Map<string, any>()
            Object.keys(document.fields).forEach(key => {
                if (key === "documentId") {
                    fieldsValue.set(key, document.documentId)
                } else if (key === "version") {
                    fieldsValue.set(key, document.version)
                    documentEditedInformation.additionalInformation.push(
                        new RqInputDocumentAdditionalInformation("version", "string", document.version))
                } else if (key === "expirationDate") {
                    fieldsValue.set(key, document.expirationDate ? new Date(document.expirationDate).toLocaleDateString("en-GB") : "")
                    documentEditedInformation.additionalInformation.push(
                        new RqInputDocumentAdditionalInformation("expirationDate", "date", document.expirationDate ?
                            new Date(document.expirationDate).toLocaleDateString("en-GB") : ""))
                } else {
                    fieldsValue.set(key, "")
                }
            })
            setFieldsValue(fieldsValue)
        }

    }, [documentEditedInformation.additionalInformation])

    const isEnabled = () => {
        return fieldsValue && fieldsValue.size > 0 && !Array.from(fieldsValue.values()).some(value => value === "")
    }

    const buildDocumentInfoBlock = (show: boolean) => {
        return (
            <div className={`info-position header-back-container ${show ? '' : 'hide-important'}`}>
                <div className={`info-container ${show ? '' : 'hide-important'}`}>
                    <div className="info-container-title primary-color font-lg full-row">
                        Document information
                    </div>
                    <InputComponent
                    label="ID type"
                    disabled
                    value={documentTypeToLabel(documentType)}
                    onChange={(value) => {}}/>
                    <InputComponent
                        label="Number"
                        value={documentNumber}
                        onlyUppercase
                        onChange={(value) => markAsEditedDocumentInfo("documentNumber", setDocumentNumber, value, "string")}/>
                    {documentType === DocumentType.NZ_DRIVERS_LICENCE.toUpperCase() ? (
                        <InputComponent
                            label="Version"
                            value={version}
                            digitsOnly
                            onChange={(value) => markAsEditedDocumentInfo("version", setVersion, value, "string")}/>
                    ) : (
                        <InputComponent
                            label="Expiration date"
                            type="date"
                            value={expirationDate}
                            onChange={(value) => markAsEditedDocumentInfo("expirationDate", setExpirationDate, value, "date")}
                            placeholder="DD/MM/YYYY"/>
                    )}

                </div>
                <div className={`width-full info-step-button-container`}>
                    <ButtonComponent label="Next" onButtonClick={() => onNextClick()} className={`info-step-button`}
                                     disabled={!isEnabled()}/>
                </div>

            </div>
        )
    }

    const documentFieldsAsObject = (): InputDocument => {
        const additionalFields: InputDocumentAdditionalInformation[] = []
        if (fields) {
            Object.entries(fields).forEach(([key,field]) => {
                const value = fieldsValue.get(key)
                if (value) {
                    additionalFields.push(new InputDocumentAdditionalInformation(
                        field.title,
                        key,
                        value
                    ))
                }
            })
        }
        return new InputDocument(
            documentNumber,
            documentType,
            additionalFields
        )
    }

    return {
        documentFieldsAsObject,
        documentEditedInformation,
        populateDocumentInformation,
        buildDocumentInfoBlock,
        buildFieldsDocumentInfoBlock
    }

}